import { useRef, useState } from 'react';

import { Checkbox } from 'design-system';
import {
  ChartNoAxesCombined,
  ChevronDown,
  GripVertical,
  List,
  Trash2,
  ExternalLink
} from 'lucide-react';
import type { DraggableEvent } from 'react-draggable';
import Draggable from 'react-draggable';

import { Z_INDEX } from '@/config/z-index';
import type { SymbolKey } from '@/entities/symbol';

import { useSymbolDragBounds } from '../hooks/useSymbolDragBounds';
import { formatSelectionLabel } from '../lib/formatSelectionLabel';
import { SELECTOR_MODES } from '../state/symbolSelector.slice';
import { CreateAppMenu } from './CreateAppMenu/CreateAppMenu';
import { SelectorButton } from './SelectorButton/SelectorButton';

type Position = { x: number; y: number };

interface SymbolSelectorToolbarProps {
  symbols: SymbolKey[];
  selectorMode: SELECTOR_MODES;
  position: Position;
  isInCatalog: boolean;
  isDashboardAppSelector: boolean;
  isVisible: boolean;
  onDrag: (e: DraggableEvent, data: Position) => void;
  onDraggingChange: (dragging: boolean) => void;
  onClear: () => void;
  onViewCatalog: () => void;
  onExport: () => void;
  onRequestExclusiveAction: (fn: () => void) => void;
  onAddToApp: () => void;
}

export const SymbolSelectorToolbar = ({
  symbols,
  selectorMode,
  position,
  isInCatalog,
  isDashboardAppSelector,
  isVisible,
  onDrag,
  onDraggingChange,
  onClear,
  onViewCatalog,
  onExport,
  onRequestExclusiveAction,
  onAddToApp
}: SymbolSelectorToolbarProps) => {
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const [createAppAnchor, setCreateAppAnchor] = useState<HTMLElement | null>(null);
  const { dragBounds, setDragging } = useSymbolDragBounds(anchorRef);

  const handleDragStart = () => {
    setDragging(true);
    onDraggingChange(true);
  };

  const handleDragStop = () => {
    setDragging(false);
    onDraggingChange(false);
  };

  const handleCreateAppClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    const target = e.currentTarget;
    onRequestExclusiveAction(() => {
      setCreateAppAnchor(prev => (prev ? null : target));
    });
  };

  return (
    <Draggable
      bounds={dragBounds}
      handle='div.cursor'
      nodeRef={anchorRef}
      position={position}
      onDrag={onDrag}
      onStart={handleDragStart}
      onStop={handleDragStop}
    >
      <div
        ref={anchorRef}
        className={`scroll--white absolute flex min-w-[354px] flex-col p-3 pr-2 shadow-[0_6px_8px_rgba(255,255,255,0.15)] ${
          isVisible ? 'flex' : 'hidden'
        } ${
          selectorMode === SELECTOR_MODES.FORWARD_CURVE
            ? 'bg-gx-selector-forward-curve'
            : 'bg-gx-highlight'
        }`}
        style={{ zIndex: Z_INDEX.priority }}
      >
        <span className='mb-2 text-[10px] font-bold uppercase'>
          {selectorMode === SELECTOR_MODES.FORWARD_CURVE ? 'Curve Mode' : 'Item Mode'}
        </span>
        <div className='flex items-center justify-around'>
          <div
            className='cursor'
            style={{ paddingRight: 8 }}
          >
            <div className='flex cursor-all-scroll'>
              <GripVertical aria-hidden='true' />
            </div>
          </div>
          <div className='flex cursor-default items-center'>
            <Checkbox
              aria-hidden='true'
              checked
              className='cursor-default opacity-100'
              disabled
              tabIndex={-1}
            />
            <div className='mr-1 flex cursor-default flex-col items-start px-2'>
              <span className='text-xs'>{formatSelectionLabel(symbols, selectorMode)}</span>
            </div>
          </div>
          <div className='flex'>
            <SelectorButton onClick={onClear}>
              <Trash2 />
              <span>Clear selection</span>
            </SelectorButton>

            {!isDashboardAppSelector && (
              <>
                <SelectorButton
                  disabled={isInCatalog}
                  onClick={onViewCatalog}
                >
                  <List />
                  <span>View in Catalog</span>
                </SelectorButton>
                <SelectorButton onClick={onExport}>
                  <ExternalLink />
                  <span>Export selection</span>
                </SelectorButton>
                <SelectorButton
                  className='justify-between'
                  onClick={handleCreateAppClick}
                >
                  <span className='flex items-center gap-1'>
                    <ChartNoAxesCombined />
                    <span>Create dashboard app</span>
                  </span>
                  <ChevronDown />
                </SelectorButton>
                <CreateAppMenu
                  containerEl={anchorRef.current}
                  open={Boolean(createAppAnchor)}
                  selectorMode={selectorMode}
                  onClose={() => {
                    setCreateAppAnchor(null);
                  }}
                />
              </>
            )}

            {isDashboardAppSelector && (
              <SelectorButton onClick={onAddToApp}>
                <List />
                <span>Add to app</span>
              </SelectorButton>
            )}
          </div>
        </div>
      </div>
    </Draggable>
  );
};
